import { useId, useState } from "react";

import { refusalText } from "../api/errors";
import type { MeView } from "../api/types";
import { Button } from "../ui/Button";
import { Pill } from "../ui/Pill";
import { useSwitchSpace } from "./auth";

/** The discipline the person works in ("Working in: Mechanical"), and the way to work in another one. It sits in the account menu of the bar. */
export function WorkspaceSwitch({ me }: { me: MeView }) {
  const id = useId();
  const { switchTo, isPending } = useSwitchSpace();
  const [problem, setProblem] = useState<string | null>(null);
  const active = me.active;
  if (me.memberships.length === 0) return null;

  const change = async (code: string, space: "workspace" | "practice" = "workspace") => {
    setProblem(null);
    try {
      await switchTo(code, space);
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  const label = (m: MeView["memberships"][number]) => `${m.workspace_name}${m.workspace_status === "closed" ? " (closed)" : ""}`;

  return (
    <div className="shell__discipline row">
      {me.memberships.length === 1 && active ? (
        <span>
          <span className="muted">Working in:</span> {label(me.memberships[0]!)}
        </span>
      ) : (
        <>
          <label htmlFor={id} className="muted">
            Working in
          </label>
          <select
            id={id}
            value={active?.workspace_code ?? ""}
            disabled={isPending}
            onChange={(event) => event.target.value && void change(event.target.value)}
          >
            {!active && <option value="">Choose one</option>}
            {me.memberships.map((membership) => (
              <option key={membership.id} value={membership.workspace_code}>
                {label(membership)}
              </option>
            ))}
          </select>
        </>
      )}
      {active?.space === "practice" && (
        <>
          <Pill>Practice space</Pill>
          <Button variant="quiet" disabled={isPending} onClick={() => void change(active.workspace_code, "workspace")}>
            Back to {active.workspace_name}
          </Button>
        </>
      )}
      {problem && (
        <span role="alert" className="form-error">
          {problem}
        </span>
      )}
    </div>
  );
}
